import { Flex, HStack, MixpanelEvent, MixpanelEventProperty, Stack, canViewDeepDive, useMixpanel } from '@talos/kyoko';
import { useEffect, useMemo } from 'react';
import { OHLCVChart } from '../../../components/OHLCVChart/OHLCVChart';
import { useDeepDiveContext } from '../../../providers/DeepDiveProvider';
import { NoMatchPage } from '../../NoMatchPage';
import { Aside } from './Aside/Aside';
import { DeepDiveHeader } from './Header/DeepDiveHeader';
import { Markets } from './Markets';
import { MouseEventCapture } from './styles';

interface DeepDiveProps {
  resizing: boolean;
}

export const DeepDive = ({ resizing }: DeepDiveProps) => {
  const mixpanel = useMixpanel();
  const {
    setChartResolution,
    chartResolution,
    showMarkets,
    security,
    selectedExchangeMarkets,
    securityExchangeMarkets,
    annotations,
    chartType,
    setAnnotations,
    setChartType,
  } = useDeepDiveContext();
  const symbol = security?.Symbol ?? '';

  useEffect(() => {
    mixpanel.track(MixpanelEvent.ViewDeepDive, { [MixpanelEventProperty.ProductType]: security?.ProductType });
  }, [mixpanel, security?.ProductType]);

  const markets = useMemo(() => {
    return selectedExchangeMarkets.length > 0 ? selectedExchangeMarkets : securityExchangeMarkets;
  }, [selectedExchangeMarkets, securityExchangeMarkets]);

  if (!security || !canViewDeepDive(security)) {
    return <NoMatchPage />;
  }

  return (
    <Stack w="100%" h="100%" key={symbol} background="backgroundBody" data-testid="deep-dive" overflow="hidden">
      <DeepDiveHeader security={security} exchangeMarkets={markets} />
      <Flex gap="spacing2" flex={1} overflow="hidden">
        <Stack zIndex={1} flex="1" gap="spacing2">
          <HStack background="backgroundContent" flex={1} position="relative" overflow="hidden">
            <OHLCVChart
              key={symbol}
              symbol={symbol}
              markets={markets}
              resolution={chartResolution ?? '1H'}
              onResolutionChange={setChartResolution}
              onAnnotationsChange={setAnnotations}
              onChartTypeChange={setChartType}
              annotations={annotations}
              chartType={chartType}
              onSymbolChange={undefined}
            />
            {resizing && <MouseEventCapture />}
          </HStack>
          {showMarkets && <Markets security={security} />}
        </Stack>
        <Aside security={security} />
      </Flex>
    </Stack>
  );
};
